import React, { useEffect, useState } from "react";

import { useSignUpStore } from "~/stores";
import {
  getAutoSelectedPlanType,
  getNextInsuranceStep,
  getPrevInsuranceStep,
  INSURANCE_FORM_STEPS,
} from "~/utils";
import { MemberIdForm } from "./Insurance";
import { AetnaPlanForm } from "./Insurance/AetnaPlanForm";
import { AnthemEmpirePosForm } from "./Insurance/AnthemEmpirePosForm";
import { BcbsPlanForm } from "./Insurance/BcbsPlanForm";
import { FidelisPlanForm } from "./Insurance/FidelisPlanForm";
import { MedicalConditionsForm } from "./Insurance/MedicalConditionsForm";
import { OtherInsuranceProviderForm } from "./Insurance/OtherInsuranceProvider";
import { PlanCoverageForm } from "./Insurance/PlanCoverageForm";
import { PlanTypeProviderForm } from "./Insurance/PlanTypeProviderForm";
import { ProviderForm } from "./Insurance/Provider";
import { UhcPlanForm } from "./Insurance/UhcPlanCoverageForm";

const formSteps: Record<INSURANCE_FORM_STEPS, React.ComponentType<any>> = {
  [INSURANCE_FORM_STEPS.PROVIDER]: ProviderForm,
  [INSURANCE_FORM_STEPS.OTHER_INSURANCE]: OtherInsuranceProviderForm,
  [INSURANCE_FORM_STEPS.PLAN_TYPE_PROVIDER]: PlanTypeProviderForm,
  [INSURANCE_FORM_STEPS.MEDICAL_CONDITIONS]: MedicalConditionsForm,
  [INSURANCE_FORM_STEPS.PLAN_COVERAGE]: PlanCoverageForm,
  [INSURANCE_FORM_STEPS.BCBS_PLAN]: BcbsPlanForm,
  [INSURANCE_FORM_STEPS.ANTHEM_EMPIRE_POS]: AnthemEmpirePosForm,
  [INSURANCE_FORM_STEPS.UHC_PLAN]: UhcPlanForm,
  [INSURANCE_FORM_STEPS.AETNA_PLAN]: AetnaPlanForm,
  [INSURANCE_FORM_STEPS.FIDELIS_PLAN]: FidelisPlanForm,
  [INSURANCE_FORM_STEPS.MEMBER_ID_FORM]: MemberIdForm,
};

interface PrimaryInsuranceProviderFormProps {
  onPrev: () => void;
  onNext: () => void;
}

export const PrimaryInsuranceProviderForm = ({
  onPrev,
  onNext,
}: PrimaryInsuranceProviderFormProps) => {
  const [currentForm, setCurrentForm] = useState<keyof typeof formSteps>(
    INSURANCE_FORM_STEPS.PROVIDER,
  );
  const {
    patient,
    showInsuranceFinalStep,
    setShowInsuranceFinalStep,
    setPatientInsuranceField,
  } = useSignUpStore();
  const { primaryInsurance, state } = patient;

  useEffect(() => {
    if (showInsuranceFinalStep) {
      setCurrentForm(INSURANCE_FORM_STEPS.MEMBER_ID_FORM);
      setShowInsuranceFinalStep(false);
    }
  }, []);

  const onNextInsuranceStep = (response?: string) => {
    const effectiveProvider =
      currentForm === INSURANCE_FORM_STEPS.PROVIDER
        ? response
        : primaryInsurance.providerName;

    // Auto-set plan type when provider has only one option
    if (currentForm === INSURANCE_FORM_STEPS.PROVIDER) {
      const autoPlanType = getAutoSelectedPlanType(effectiveProvider);
      if (autoPlanType) {
        setPatientInsuranceField("planTypeProvider", autoPlanType);
      }
    }

    const nextStep = getNextInsuranceStep(
      currentForm,
      response,
      primaryInsurance.providerName,
      primaryInsurance.planTypeProvider,
      state,
      primaryInsurance.planCoverage,
      primaryInsurance.bcbsProvider,
      false,
    );
    if (nextStep) {
      setCurrentForm(nextStep);
    } else {
      onNext();
    }
  };

  const onPrevInsuranceStep = () => {
    if (currentForm === INSURANCE_FORM_STEPS.PROVIDER) {
      onPrev();
    } else {
      const prevStep = getPrevInsuranceStep(
        currentForm,
        primaryInsurance.providerName,
        primaryInsurance.otherProvider,
        primaryInsurance.planTypeProvider,
        state,
        primaryInsurance.planCoverage,
        primaryInsurance.bcbsProvider,
        false,
      );
      if (prevStep) setCurrentForm(prevStep);
    }
  };

  const CurrentComponent = formSteps[currentForm];

  return (
    <CurrentComponent
      onPrev={onPrevInsuranceStep}
      onNext={onNextInsuranceStep}
    />
  );
};
